import type { ComponentType, ReactNode } from "react";
import { useState } from "react";
import { UserSettingsDialog } from "./UserSettingsDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/data-states";
import { ArrowLeft, ChevronRight, LogOut, PanelLeftClose } from "lucide-react";
import { cn } from "@/lib/utils";
import { APP_VERSION } from "@/lib/config";
import { SozuLogo } from "@/components/ui/sozu-logo";
import { usePerfilVista } from "@/hooks/usePerfilVista";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { useAvatarVista } from "@/hooks/useAvatarVista";
import { AvatarPersona } from "@/components/ui/avatar-persona";

/**
 * Sidebar estándar de los portales (DESIGN §1): marca, contexto opcional, navegación y pie
 * con usuario, Regresar, Cerrar sesión y versión. Sin líneas divisoras internas: los bloques
 * se separan con espacio. El layout lo monta igual en el `aside` de escritorio
 * (`PORTAL_SIDEBAR_ASIDE_CLS`) y en el `Sheet` de celular.
 */

export interface PortalSidebarItem {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  active: boolean;
  onSelect: () => void;
  disabled?: boolean;
  /** Contador o etiqueta corta a la derecha. */
  badge?: ReactNode;
}

export interface PortalSidebarGroup {
  /** Título del grupo; sin título se dibuja como lista plana. */
  label?: string;
  items: PortalSidebarItem[];
}

export interface PortalSidebarProps {
  /** "Portal Cobranza", "Portal de Agentes"... */
  portal: string;
  /** Bloque entre la marca y el menú: selector de proyecto, datos de la inmobiliaria. */
  contexto?: ReactNode;
  groups: PortalSidebarGroup[];
  cargando?: boolean;
  /** Línea secundaria del vacío; por defecto "El rol <rol> no tiene accesos aquí". "" oculta el vacío. */
  vacio?: string;
  /** El bloque del usuario del pie abre "Mi cuenta" (o su ficha en solo lectura con "Ver como"). Hoy solo en los portales que lo piden. */
  miCuenta?: boolean;
  usuario: {
    nombre: string;
    rol?: string | null;
    /** Avatar propio (foto); si no, círculo con iniciales. */
    avatar?: ReactNode;
    iniciales?: string;
    /** Foto de perfil; si no se pasa, sale la del usuario de la sesión (o la del impersonado). */
    fotoUrl?: string | null;
    onClick?: () => void;
  };
  /** Marca clicable (p. ej. vuelve al inicio del portal). */
  onMarca?: () => void;
  /** Siempre dice "Regresar" (vuelve al admin). */
  onRegresar?: () => void;
  onCerrarSesion: () => void;
}

/** Clases del `aside` de escritorio, iguales en todos los portales. */
export const PORTAL_SIDEBAR_ASIDE_CLS =
  "hidden lg:flex lg:flex-col border-r border-border bg-sidebar fixed inset-y-0 left-0 z-30 w-64";
/** Clases del `SheetContent` de celular. */
export const PORTAL_SIDEBAR_SHEET_CLS = "flex w-64 flex-col bg-sidebar p-0";

function Item({ item }: { item: PortalSidebarItem }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={item.onSelect}
      disabled={item.disabled}
      aria-current={item.active ? "page" : undefined}
      className={cn(
        "group relative flex w-full items-center gap-3 rounded-md py-2 pl-4 pr-3 text-left text-[13px] font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50",
        item.active
          ? "bg-primary/[0.06] text-primary"
          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
      )}
    >
      <span
        className={cn(
          "absolute bottom-0 left-0 top-0 w-[2px] rounded-r bg-primary transition-opacity duration-150",
          item.active ? "opacity-100" : "opacity-0",
        )}
      />
      <Icon
        className={cn(
          "size-4 shrink-0",
          !item.active && "opacity-60 transition-opacity duration-150 group-hover:opacity-100",
        )}
      />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.badge != null && <span className="shrink-0">{item.badge}</span>}
    </button>
  );
}

export function PortalSidebar({
  portal,
  contexto,
  groups,
  cargando = false,
  vacio,
  miCuenta = false,
  usuario: usuarioSesion,
  onMarca,
  onRegresar,
  onCerrarSesion,
}: PortalSidebarProps) {
  // Con "Ver como" el pie muestra a la persona impersonada (lo que ella vería), no al admin.
  const perfil = usePerfilVista({ nombre: usuarioSesion.nombre, rol: usuarioSesion.rol });
  const usuario: PortalSidebarProps["usuario"] = perfil.impersonando
    ? { nombre: perfil.nombre, rol: perfil.rol, iniciales: perfil.iniciales }
    : usuarioSesion;
  const fotoVista = useFotoPerfilVista();
  const avatarVista = useAvatarVista();
  const conItems = groups.filter((g) => g.items.length > 0);
  const [cuentaAbierta, setCuentaAbierta] = useState(false);
  const alClic = miCuenta ? () => setCuentaAbierta(true) : usuario.onClick;
  const UsuarioTag = alClic ? "button" : "div";
  const marca = (
    <>
      <SozuLogo className="h-6" />
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500">{portal}</p>
    </>
  );

  return (
    <>
      {onMarca ? (
        <button type="button" onClick={onMarca} className="flex flex-col gap-1 px-5 pb-4 pt-4 text-left transition-colors hover:bg-muted/50">
          {marca}
        </button>
      ) : (
        <div className="flex flex-col gap-1 px-5 pb-4 pt-4">{marca}</div>
      )}

      {contexto && <div className="px-4 pb-3">{contexto}</div>}

      <nav className="flex-1 space-y-3 overflow-y-auto px-3 py-2">
        {cargando ? (
          <div className="space-y-1 px-1" aria-hidden>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9 bg-muted/50" />
            ))}
          </div>
        ) : conItems.length === 0 ? (
          vacio === "" ? null : (
            <EmptyState
              compacto
              icono={PanelLeftClose}
              titulo="Sin menús en este portal"
              texto={vacio ?? (perfil.rol ? `El rol ${perfil.rol} no tiene accesos aquí` : "Este rol no tiene accesos aquí")}
            />
          )
        ) : (
          conItems.map((g, i) => (
            <div key={g.label ?? `grupo-${i}`}>
              {g.label && (
                <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
                  {g.label}
                </p>
              )}
              <div className="space-y-0.5">
                {g.items.map((item) => <Item key={item.key} item={item} />)}
              </div>
            </div>
          ))
        )}
      </nav>

      <div className="space-y-1 px-3 pb-4 pt-3">
        <UsuarioTag
          {...(alClic ? { type: "button" as const, onClick: alClic } : {})}
          className={cn(
            "group/perfil flex w-full items-center gap-3 rounded-md px-2 py-2 text-left",
            alClic && "transition-colors hover:bg-muted/60",
          )}
        >
          {usuario.avatar ?? (
            miCuenta ? (
              <AvatarPersona nombre={usuario.nombre} fotoUrl={usuario.fotoUrl ?? avatarVista.fotoUrl} cargando={avatarVista.cargando} enLinea={avatarVista.enLinea} ultimaActividad={avatarVista.ultimaActividad} size="sm" />
            ) : (
              <AvatarPersona nombre={usuario.nombre} fotoUrl={usuario.fotoUrl ?? fotoVista} enLinea={avatarVista.enLinea} ultimaActividad={avatarVista.ultimaActividad} size="sm" />
            )
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-foreground">{usuario.nombre}</span>
            {usuario.rol && <span className="block truncate text-xs text-muted-foreground">{usuario.rol}</span>}
          </span>
          {alClic && (
            <ChevronRight className="size-4 text-muted-foreground opacity-0 transition-opacity group-hover/perfil:opacity-100" />
          )}
        </UsuarioTag>
        {miCuenta && <UserSettingsDialog open={cuentaAbierta} onOpenChange={setCuentaAbierta} />}

        <div className="flex gap-2">
          {onRegresar && (
            <button
              type="button"
              onClick={onRegresar}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
            >
              <ArrowLeft className="size-4 shrink-0" />
              Regresar
            </button>
          )}
          <button
            type="button"
            onClick={onCerrarSesion}
            className={cn(
              "flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-destructive transition-colors hover:bg-destructive/10",
              onRegresar ? "flex-1" : "w-full",
            )}
          >
            <LogOut className="size-4 shrink-0" />
            Cerrar sesión
          </button>
        </div>

        <p className="pt-0.5 text-center font-mono text-[10px] text-muted-foreground/40">{APP_VERSION}</p>
      </div>
    </>
  );
}
